define([
    '../Util/Check',
    '../Util/defaultValue',
    '../Util/defined'
], function (
    Check,
    defaultValue,
    defined) {
    'use strict'

    //#rgb
    var rgbMatcher = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i
    //#rrggbb
    var rrggbbMatcher = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i
    //rgb(), rgba(), or rgb%()
    var rgbParenthesesMatcher = /^rgba?\(\s*([0-9.]+%?)\s*,\s*([0-9.]+%?)\s*,\s*([0-9.]+%?)(?:\s*,\s*([0-9.]+))?\s*\)$/i
    //hsl(), hsla(), or hsl%()
    var hslParenthesesMatcher = /^hsla?\(\s*([0-9.]+)\s*,\s*([0-9.]+%)\s*,\s*([0-9.]+%)(?:\s*,\s*([0-9.]+))?\s*\)$/i
    const hue2rgb = (m1, m2, h) => {
        if (h < 0) {
            h += 1
        }
        if (h > 1) {
            h -= 1
        }
        if (h * 6 < 1) {
            return m1 + (m2 - m1) * 6 * h
        }
        if (h * 2 < 1) {
            return m2
        }
        if (h * 3 < 2) {
            return m1 + (m2 - m1) * (2 / 3 - h) * 6
        }
        return m1
    }
    class ColorObj {
        /**
         * @description 在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "ColorObj"
        /**
         * 颜色类型(RGBA)。
         * @param {Number} red=1.0 红色值。范围0.0-1.0。
         * @param {Number} green=1.0 绿色值。范围0.0-1.0。
         * @param {Number} blue=1.0 蓝色值。范围0.0-1.0。
         * @param {Number} alpha=1.0 透明度值。范围0.0-1.0。
         * 
         * @alias ColorObj
         * @constructor
         *
         */
        constructor(red, green, blue, alpha) {
            /**
             * 红色值。范围0.0-1.0。
             * @type {Number}
             * @default 1.0
             */
            this.red = defaultValue(red, 1.0)
            /**
             * 绿色值。范围0.0-1.0。
             * @type {Number}
             * @default 1.0
             */
            this.green = defaultValue(green, 1.0)
            /**
             * 蓝色值。范围0.0-1.0。
             * @type {Number}
             * @default 1.0
             */
            this.blue = defaultValue(blue, 1.0)
            /**
             * 透明度值。范围0.0-1.0。
             * @type {Number}
             * @default 1.0
             */
            this.alpha = defaultValue(alpha, 1.0)
        }

        /**
         * 创建一个新的 ColorObj 对象，该对象使用指定的红、绿、蓝和透明度值，这些值的范围是 0 到 255，并将它们内部转换为 0.0 到 1.0 的范围。
         *
         * @param {Number} alpha=255 透明度值。
         * @param {Number} blue=255 蓝色值。
         * @param {Number} green=255 绿色值。
         * @param {Number} red=255 红色值。
         * @param {ColorObj} [result] 存储ColorObj实例的对象。
         * @returns {ColorObj} 如果没有提供修改后的结果参数，则提供一个新的ColorObj实例。
         * @static
         * 
         * @example
         * var newColor = CooWasm.ColorObj.fromBytes(123, 123, 255, 0)
         * 
         */
        static fromBytes(red, green, blue, alpha, result) {
            red = ColorObj.byteToFloat(defaultValue(red, 255.0))
            green = ColorObj.byteToFloat(defaultValue(green, 255.0))
            blue = ColorObj.byteToFloat(defaultValue(blue, 255.0))
            alpha = ColorObj.byteToFloat(defaultValue(alpha, 255.0))

            if (!defined(result)) {
                return new ColorObj(red, green, blue, alpha)
            }

            result.red = red
            result.green = green
            result.blue = blue
            result.alpha = alpha
            return result
        }

        /**
         * 从CSS颜色值创建一个ColorObj实例。
         *
         * @param {String} color #rgb、#rrggbb、rgb()、rgba()、hsl()或hsla()格式的CSS颜色值。
         * @param {ColorObj} [result] 如果未定义，将创建一个ColorObj新实例。
         * @returns {ColorObj} 如果字符串不是有效的CSS颜色，则为ColorObj对象，或未定义。
         * @static
         *
         *
         * @example
         * var newColor = CooWasm.ColorObj.fromCssColorString('#67ADDF')
         * var newColor1 = CooWasm.ColorObj.fromCssColorString('green')
         * @see {@link http://www.w3.org/TR/css3-color|CSS color values}
         */
        static fromCssColorString(color, result) {
            Check.typeOf.string('color', color)

            if (!defined(result)) {
                result = new ColorObj()
            }

            var namedColor = ColorObj[color.toUpperCase()]
            if (defined(namedColor)) {
                ColorObj.clone(namedColor, result)
                return result
            }

            var matches = rgbMatcher.exec(color)
            if (matches !== null) {
                result.red = parseInt(matches[1], 16) / 15
                result.green = parseInt(matches[2], 16) / 15.0
                result.blue = parseInt(matches[3], 16) / 15.0
                result.alpha = 1.0
                return result
            }
            
            var matches = color.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i)
            if (matches !== null) {
                result.red = parseInt(matches[1], 16) / 255.0
                result.green = parseInt(matches[2], 16) / 255.0
                result.blue = parseInt(matches[3], 16) / 255.0
                result.alpha = parseInt(matches[4], 16) / 255.0
                return result
            }

            matches = rrggbbMatcher.exec(color)
            if (matches !== null) {
                result.red = parseInt(matches[1], 16) / 255.0
                result.green = parseInt(matches[2], 16) / 255.0
                result.blue = parseInt(matches[3], 16) / 255.0
                result.alpha = 1.0
                return result
            }

            matches = rgbParenthesesMatcher.exec(color)
            if (matches !== null) {
                result.red = parseFloat(matches[1]) / ('%' === matches[1].substr(-1) ? 100.0 : 255.0)
                result.green = parseFloat(matches[2]) / ('%' === matches[2].substr(-1) ? 100.0 : 255.0)
                result.blue = parseFloat(matches[3]) / ('%' === matches[3].substr(-1) ? 100.0 : 255.0)
                result.alpha = parseFloat(defaultValue(matches[4], '1.0'))
                return result
            }

            matches = hslParenthesesMatcher.exec(color)
            if (matches !== null) {
                return ColorObj.fromHsl(parseFloat(matches[1]) / 360.0,
                    parseFloat(matches[2]) / 100.0,
                    parseFloat(matches[3]) / 100.0,
                    parseFloat(defaultValue(matches[4], '1.0')), result)
            }

            result = undefined
            return result
        }
        /**
         * 将ColorObj实例转换成"R G B A"格式的字符串。
         *
         * @returns {String} "R G B A"格式的字符串。
         */
        toStr() {
            var result = String(this.red) + ' ' + String(this.green) + ' ' + String(this.blue) + ' ' + String(this.alpha)
            return result
        }

        toLilGuiStr() {
            var result = "rgb(" + String(Math.round(this.red * 255)) + "," + String(Math.round(this.green * 255)) + "," + String(Math.round(this.blue * 255)) + ")"
            return result
        }
        /**
         * 将ColorObj实例转换为十六进制字符串。
         *
         * @returns {String} 十六进制字符串。
         * @example
         * var color = new CooWasm.ColorObj(0.5, 0.5, 0.5, 1.0)
         * var hexColor = color.toHexStr() // "#7f7f7f"
         */
        toHexStr() {
            var red = Math.round(this.red * 255).toString(16).padStart(2, '0')
            var green = Math.round(this.green * 255).toString(16).padStart(2, '0')
            var blue = Math.round(this.blue * 255).toString(16).padStart(2, '0')
            var alpha = Math.round(this.alpha * 255).toString(16).padStart(2, '0')
            return `#${red}${green}${blue}${alpha}`
        }
        // 将RGB字符串转换为0-1范围的RGB值并赋值
        fromLilGuiStr(rgbStr) {
            const match = rgbStr.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/)
            if (match) {
                this.red = parseInt(match[1], 10) / 255
                this.green = parseInt(match[2], 10) / 255
                this.blue = parseInt(match[3], 10) / 255
            }
        }
        /**
         * 将"R G B A"格式的字符串转换成ColorObj实例。
         *
         * @param {String} str "R G B A"格式的字符串。
         * @param {ColorObj} [result] 如果未定义，将创建一个ColorObj新实例。
         * @returns {ColorObj} ColorObj实例。
         */
        static fromStr(str, result) {
            Check.typeOf.string('str', str)
            //>>includeEnd('debug')

            if (!defined(result)) {
                result = new ColorObj()
            }
            var info = str.split(' ')
            result.red = parseFloat(info[0])
            result.green = parseFloat(info[1])
            result.blue = parseFloat(info[2])
            result.alpha = parseFloat(info[3])
            return result
        }

        /**
         * 从色调、饱和度和亮度创建一个ColorObj实例。
         *
         * @param {Number} alpha=1.0 透明度值。0-1。
         * @param {Number} hue=0 色调值。0-1。
         * @param {Number} lightness=0 明度值。0-1。
         * @param {ColorObj} [result] 如果未定义，将创建一个新的ColorObj实例。
         * @param {Number} saturation=0 饱和值。0-1。
         * @returns {ColorObj} 颜色对象。
         *
         * @see {@link http://www.w3.org/TR/css3-color/#hsl-color|CSS color values}
         * 
         * @example
         * var newColor = CooWasm.ColorObj.fromHsl(0.5, 0.3, 0.2, 0.1)
         * 
         */
        static fromHsl(hue, saturation, lightness, alpha, result) {
            hue = defaultValue(hue, 0.0) % 1.0
            saturation = defaultValue(saturation, 0.0)
            lightness = defaultValue(lightness, 0.0)
            alpha = defaultValue(alpha, 1.0)

            var red = lightness
            var green = lightness
            var blue = lightness

            if (saturation !== 0) {
                var m2
                if (lightness < 0.5) {
                    m2 = lightness * (1 + saturation)
                } else {
                    m2 = lightness + saturation - lightness * saturation
                }

                var m1 = 2.0 * lightness - m2
                red = hue2rgb(m1, m2, hue + 1 / 3)
                green = hue2rgb(m1, m2, hue)
                blue = hue2rgb(m1, m2, hue - 1 / 3)
            }

            if (!defined(result)) {
                return new ColorObj(red, green, blue, alpha)
            }

            result.red = red
            result.green = green
            result.blue = blue
            result.alpha = alpha
            return result
        }

        /**
         * 将0到255范围内的“byte”颜色值转换为0到1.0范围内的“float”颜色值。
         *
         * @param {Number} number 要转换的数值。
         * @returns {Number} 转换后的数值。
         */
        static byteToFloat(number) {
            return number / 255.0
        }

        /**
         * 复制ColorObj对象。
         *
         * @param {ColorObj} color 要复制的ColorObj。
         * @param {ColorObj} [result] 如果未定义，将创建一个新的ColorObj实例。
         * @returns {ColorObj} 如果结果未定义，则修改结果参数或新的ColorObj实例。(如果颜色未定义，则返回未定义)。
         */
        static clone(color, result) {
            if (!defined(color)) {
                return undefined
            }
            if (!defined(result)) {
                return new ColorObj(color.red, color.green, color.blue, color.alpha)
            }
            result.red = color.red
            result.green = color.green
            result.blue = color.blue
            result.alpha = color.alpha
            return result
        }
    }
    ColorObj.ALICEBLUE = ColorObj.fromBytes(240, 248, 255);
    ColorObj.ANTIQUEWHITE = ColorObj.fromBytes(250, 235, 215);
    ColorObj.AQUA = ColorObj.fromBytes(0, 255, 255);
    ColorObj.AQUAMARINE = ColorObj.fromBytes(127, 255, 212);
    ColorObj.AZURE = ColorObj.fromBytes(240, 255, 255);
    ColorObj.BEIGE = ColorObj.fromBytes(245, 245, 220);
    ColorObj.BISQUE = ColorObj.fromBytes(255, 228, 196);
    ColorObj.BLACK = ColorObj.fromBytes(0, 0, 0);
    ColorObj.BLANCHEDALMOND = ColorObj.fromBytes(255, 235, 205);
    ColorObj.BLUE = ColorObj.fromBytes(0, 0, 255);
    ColorObj.BLUEVIOLET = ColorObj.fromBytes(138, 43, 226);
    ColorObj.BROWN = ColorObj.fromBytes(165, 42, 42);
    ColorObj.BURLYWOOD = ColorObj.fromBytes(222, 184, 135);
    ColorObj.CADETBLUE = ColorObj.fromBytes(95, 158, 160);
    ColorObj.CHARTREUSE = ColorObj.fromBytes(127, 255, 0);
    ColorObj.CHOCOLATE = ColorObj.fromBytes(210, 105, 30);
    ColorObj.CORAL = ColorObj.fromBytes(255, 127, 80);
    ColorObj.CORNFLOWERBLUE = ColorObj.fromBytes(100, 149, 237);
    ColorObj.CORNSILK = ColorObj.fromBytes(255, 248, 220);
    ColorObj.CRIMSON = ColorObj.fromBytes(220, 20, 60);
    ColorObj.CYAN = ColorObj.fromBytes(0, 255, 255);
    ColorObj.DARKBLUE = ColorObj.fromBytes(0, 0, 139);
    ColorObj.DARKCYAN = ColorObj.fromBytes(0, 139, 139);
    ColorObj.DARKGOLDENROD = ColorObj.fromBytes(184, 134, 11);
    ColorObj.DARKGRAY = ColorObj.fromBytes(169, 169, 169);
    ColorObj.DARKGREEN = ColorObj.fromBytes(0, 100, 0);
    ColorObj.DARKGREY = ColorObj.fromBytes(169, 169, 169);
    ColorObj.DARKKHAKI = ColorObj.fromBytes(189, 183, 107);
    ColorObj.DARKMAGENTA = ColorObj.fromBytes(139, 0, 139);
    ColorObj.DARKOLIVEGREEN = ColorObj.fromBytes(85, 107, 47);
    ColorObj.DARKORANGE = ColorObj.fromBytes(255, 140, 0);
    ColorObj.DARKORCHID = ColorObj.fromBytes(153, 50, 204);
    ColorObj.DARKRED = ColorObj.fromBytes(139, 0, 0);
    ColorObj.DARKSALMON = ColorObj.fromBytes(233, 150, 122);
    ColorObj.DARKSEAGREEN = ColorObj.fromBytes(143, 188, 143);
    ColorObj.DARKSLATEBLUE = ColorObj.fromBytes(72, 61, 139);
    ColorObj.DARKSLATEGRAY = ColorObj.fromBytes(47, 79, 79);
    ColorObj.DARKSLATEGREY = ColorObj.fromBytes(47, 79, 79);
    ColorObj.DARKTURQUOISE = ColorObj.fromBytes(0, 206, 209);
    ColorObj.DARKVIOLET = ColorObj.fromBytes(148, 0, 211);
    ColorObj.DEEPPINK = ColorObj.fromBytes(255, 20, 147);
    ColorObj.DEEPSKYBLUE = ColorObj.fromBytes(0, 191, 255);
    ColorObj.DIMGRAY = ColorObj.fromBytes(105, 105, 105);
    ColorObj.DIMGREY = ColorObj.fromBytes(105, 105, 105);
    ColorObj.DODGERBLUE = ColorObj.fromBytes(30, 144, 255);
    ColorObj.FIREBRICK = ColorObj.fromBytes(178, 34, 34);
    ColorObj.FLORALWHITE = ColorObj.fromBytes(255, 250, 240);
    ColorObj.FORESTGREEN = ColorObj.fromBytes(34, 139, 34);
    ColorObj.FUCHSIA = ColorObj.fromBytes(255, 0, 255);
    ColorObj.GAINSBORO = ColorObj.fromBytes(220, 220, 220);
    ColorObj.GHOSTWHITE = ColorObj.fromBytes(248, 248, 255);
    ColorObj.GOLD = ColorObj.fromBytes(255, 215, 0);
    ColorObj.GOLDENROD = ColorObj.fromBytes(218, 165, 32);
    ColorObj.GRAY = ColorObj.fromBytes(128, 128, 128);
    ColorObj.GREEN = ColorObj.fromBytes(0, 128, 0);
    ColorObj.GREENYELLOW = ColorObj.fromBytes(173, 255, 47);
    ColorObj.GREY = ColorObj.fromBytes(128, 128, 128);
    ColorObj.HONEYDEW = ColorObj.fromBytes(240, 255, 240);
    ColorObj.HOTPINK = ColorObj.fromBytes(255, 105, 180);
    ColorObj.INDIANRED = ColorObj.fromBytes(205, 92, 92);
    ColorObj.INDIGO = ColorObj.fromBytes(75, 0, 130);
    ColorObj.IVORY = ColorObj.fromBytes(255, 255, 240);
    ColorObj.KHAKI = ColorObj.fromBytes(240, 230, 140);
    ColorObj.LAVENDER = ColorObj.fromBytes(230, 230, 250);
    ColorObj.LAVENDERBLUSH = ColorObj.fromBytes(255, 240, 245);
    ColorObj.LAWNGREEN = ColorObj.fromBytes(124, 252, 0);
    ColorObj.LEMONCHIFFON = ColorObj.fromBytes(255, 250, 205);
    ColorObj.LIGHTBLUE = ColorObj.fromBytes(173, 216, 230);
    ColorObj.LIGHTCORAL = ColorObj.fromBytes(240, 128, 128);
    ColorObj.LIGHTCYAN = ColorObj.fromBytes(224, 255, 255);
    ColorObj.LIGHTGOLDENRODYELLOW = ColorObj.fromBytes(250, 250, 210);
    ColorObj.LIGHTGRAY = ColorObj.fromBytes(211, 211, 211);
    ColorObj.LIGHTGREEN = ColorObj.fromBytes(144, 238, 144);
    ColorObj.LIGHTGREY = ColorObj.fromBytes(211, 211, 211);
    ColorObj.LIGHTPINK = ColorObj.fromBytes(255, 182, 193);
    ColorObj.LIGHTSALMON = ColorObj.fromBytes(255, 160, 122);
    ColorObj.LIGHTSEAGREEN = ColorObj.fromBytes(32, 178, 170);
    ColorObj.LIGHTSKYBLUE = ColorObj.fromBytes(135, 206, 250);
    ColorObj.LIGHTSLATEGRAY = ColorObj.fromBytes(119, 136, 153);
    ColorObj.LIGHTSLATEGREY = ColorObj.fromBytes(119, 136, 153);
    ColorObj.LIGHTSTEELBLUE = ColorObj.fromBytes(176, 196, 222);
    ColorObj.LIGHTYELLOW = ColorObj.fromBytes(255, 255, 224);
    ColorObj.LIME = ColorObj.fromBytes(0, 255, 0);
    ColorObj.LIMEGREEN = ColorObj.fromBytes(50, 205, 50);
    ColorObj.LINEN = ColorObj.fromBytes(250, 240, 230);
    ColorObj.MAGENTA = ColorObj.fromBytes(255, 0, 255);
    ColorObj.MAROON = ColorObj.fromBytes(128, 0, 0);
    ColorObj.MEDIUMAQUAMARINE = ColorObj.fromBytes(102, 205, 170);
    ColorObj.MEDIUMBLUE = ColorObj.fromBytes(0, 0, 205);
    ColorObj.MEDIUMORCHID = ColorObj.fromBytes(186, 85, 211);
    ColorObj.MEDIUMPURPLE = ColorObj.fromBytes(147, 112, 219);
    ColorObj.MEDIUMSEAGREEN = ColorObj.fromBytes(60, 179, 113);
    ColorObj.MEDIUMSLATEBLUE = ColorObj.fromBytes(123, 104, 238);
    ColorObj.MEDIUMSPRINGGREEN = ColorObj.fromBytes(0, 250, 154);
    ColorObj.MEDIUMTURQUOISE = ColorObj.fromBytes(72, 209, 204);
    ColorObj.MEDIUMVIOLETRED = ColorObj.fromBytes(199, 21, 133);
    ColorObj.MIDNIGHTBLUE = ColorObj.fromBytes(25, 25, 112);
    ColorObj.MINTCREAM = ColorObj.fromBytes(245, 255, 250);
    ColorObj.MISTYROSE = ColorObj.fromBytes(255, 228, 225);
    ColorObj.MOCCASIN = ColorObj.fromBytes(255, 228, 181);
    ColorObj.NAVAJOWHITE = ColorObj.fromBytes(255, 222, 173);
    ColorObj.NAVY = ColorObj.fromBytes(0, 0, 128);
    ColorObj.OLDLACE = ColorObj.fromBytes(253, 245, 230);
    ColorObj.OLIVE = ColorObj.fromBytes(128, 128, 0);
    ColorObj.OLIVEDRAB = ColorObj.fromBytes(107, 142, 35);
    ColorObj.ORANGE = ColorObj.fromBytes(255, 165, 0);
    ColorObj.ORANGERED = ColorObj.fromBytes(255, 69, 0);
    ColorObj.ORCHID = ColorObj.fromBytes(218, 112, 214);
    ColorObj.PALEGOLDENROD = ColorObj.fromBytes(238, 232, 170);
    ColorObj.PALEGREEN = ColorObj.fromBytes(152, 251, 152);
    ColorObj.PALETURQUOISE = ColorObj.fromBytes(175, 238, 238);
    ColorObj.PALEVIOLETRED = ColorObj.fromBytes(219, 112, 147);
    ColorObj.PAPAYAWHIP = ColorObj.fromBytes(255, 239, 213);
    ColorObj.PEACHPUFF = ColorObj.fromBytes(255, 218, 185);
    ColorObj.PERU = ColorObj.fromBytes(205, 133, 63);
    ColorObj.PINK = ColorObj.fromBytes(255, 192, 203);
    ColorObj.PLUM = ColorObj.fromBytes(221, 160, 221);
    ColorObj.POWDERBLUE = ColorObj.fromBytes(176, 224, 230);
    ColorObj.PURPLE = ColorObj.fromBytes(128, 0, 128);
    ColorObj.RED = ColorObj.fromBytes(255, 0, 0);
    ColorObj.ROSYBROWN = ColorObj.fromBytes(188, 143, 143);
    ColorObj.ROYALBLUE = ColorObj.fromBytes(65, 105, 225);
    ColorObj.SADDLEBROWN = ColorObj.fromBytes(139, 69, 19);
    ColorObj.SALMON = ColorObj.fromBytes(250, 128, 114);
    ColorObj.SANDYBROWN = ColorObj.fromBytes(244, 164, 96);
    ColorObj.SEAGREEN = ColorObj.fromBytes(46, 139, 87);
    ColorObj.SEASHELL = ColorObj.fromBytes(255, 245, 238);
    ColorObj.SIENNA = ColorObj.fromBytes(160, 82, 45);
    ColorObj.SILVER = ColorObj.fromBytes(192, 192, 192);
    ColorObj.SKYBLUE = ColorObj.fromBytes(135, 206, 235);
    ColorObj.SLATEBLUE = ColorObj.fromBytes(106, 90, 205);
    ColorObj.SLATEGRAY = ColorObj.fromBytes(112, 128, 144);
    ColorObj.SLATEGREY = ColorObj.fromBytes(112, 128, 144);
    ColorObj.SNOW = ColorObj.fromBytes(255, 250, 250);
    ColorObj.SPRINGGREEN = ColorObj.fromBytes(0, 255, 127);
    ColorObj.STEELBLUE = ColorObj.fromBytes(70, 130, 180);
    ColorObj.TAN = ColorObj.fromBytes(210, 180, 140);
    ColorObj.TEAL = ColorObj.fromBytes(0, 128, 128);
    ColorObj.THISTLE = ColorObj.fromBytes(216, 191, 216);
    ColorObj.TOMATO = ColorObj.fromBytes(255, 99, 71);
    ColorObj.TURQUOISE = ColorObj.fromBytes(64, 224, 208);
    ColorObj.VIOLET = ColorObj.fromBytes(238, 130, 238);
    ColorObj.WHEAT = ColorObj.fromBytes(245, 222, 179);
    ColorObj.WHITE = ColorObj.fromBytes(255, 255, 255);
    ColorObj.WHITESMOKE = ColorObj.fromBytes(245, 245, 245);
    ColorObj.YELLOW = ColorObj.fromBytes(255, 255, 0);
    ColorObj.YELLOWGREEN = ColorObj.fromBytes(154, 205, 50);
    return ColorObj
})